import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube, Twitter, Phone, Mail, MapPin } from "lucide-react";
import { useContent } from "@/context/ContentContext";

const NAV_KEYS = [
  { id: "about", label: "About" },
  { id: "courses", label: "Courses" },
  { id: "consultancy", label: "Consultancy" },
  { id: "whyChooseUs", label: "Why Us" },
  { id: "faculty", label: "Faculty" },
  { id: "gallery", label: "Gallery" },
  { id: "testimonials", label: "Testimonials" },
  { id: "colleges", label: "Colleges" },
  { id: "contact", label: "Contact" },
];

const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, youtube: Youtube, twitter: Twitter };

export default function Footer() {
  const { content } = useContent();
  if (!content) return null;
  const sections = content.sections || {};
  const branding = content.branding || {};
  const footer = sections.footer || {};
  const contact = sections.contact || {};
  const navItems = NAV_KEYS.filter((n) => sections[n.id]?.visible !== false);

  return (
    <footer data-testid="site-footer" className="bg-[#0B1736] text-slate-300">
      <div className="container-pad py-16 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="flex items-center gap-3">
            {branding.logoUrl ? (
              <img
                src={branding.logoUrl}
                alt={branding.siteName || "Logo"}
                className="h-12 w-auto object-contain bg-white/5 rounded-lg p-1"
                data-testid="footer-logo-img"
              />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-teal-500 grid place-items-center text-white font-bold">S</div>
            )}
            <div>
              <div className="font-extrabold text-white text-lg">{branding.logoText || "Sunbright"}</div>
              <div className="text-xs text-slate-400 uppercase tracking-wider">{branding.logoAccent || "Pharma Academy"}</div>
            </div>
          </div>
          <p className="mt-5 text-sm leading-relaxed text-slate-400 max-w-sm">{footer.tagline}</p>
          <div className="mt-5 flex items-center gap-3">
            {Object.entries(footer.socials || {}).map(([k, v]) => {
              const Icon = SOCIAL_ICONS[k];
              if (!Icon || !v) return null;
              return (
                <a
                  key={k}
                  href={v}
                  target="_blank"
                  rel="noreferrer"
                  className="w-9 h-9 rounded-full bg-white/5 hover:bg-teal-500 text-slate-300 hover:text-white grid place-items-center transition"
                  data-testid={`footer-social-${k}`}
                  aria-label={k}
                >
                  <Icon className="w-4 h-4" />
                </a>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="text-xs uppercase tracking-wider text-teal-300 font-semibold">Quick Links</div>
          <ul className="mt-4 grid grid-cols-2 gap-2 text-sm">
            {navItems.map((n) => (
              <li key={n.id}>
                <a href={`#${n.id}`} className="hover:text-teal-300 transition" data-testid={`footer-nav-${n.id}`}>
                  {n.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-wider text-teal-300 font-semibold">Reach Us</div>
          <ul className="mt-4 space-y-3 text-sm">
            {(contact.phone || branding.primaryPhone) && (
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-0.5 text-teal-300" />
                <a href={`tel:${contact.phone || branding.primaryPhone || ""}`} className="hover:text-teal-300" data-testid="footer-phone">
                  {contact.phone || branding.primaryPhone}
                </a>
              </li>
            )}
            {contact.email && (
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-0.5 text-teal-300" />
                <a href={`mailto:${contact.email}`} className="hover:text-teal-300" data-testid="footer-email">
                  {contact.email}
                </a>
              </li>
            )}
            {contact.address && (
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-teal-300" />
                <span>{contact.address}</span>
              </li>
            )}
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-pad py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs">
          <div className="text-slate-400">{footer.copyright}</div>
          <Link
            to="/admin/login"
            data-testid="staff-login-link"
            className="text-slate-400 hover:text-teal-300 transition font-medium"
          >
            Staff Login →
          </Link>
        </div>
      </div>
    </footer>
  );
}
